import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

const CART_STORAGE_KEY = 'sapori1989_cart';
const FULFILLMENT_STORAGE_KEY = 'sapori1989_fulfillment';

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [fulfillmentType, setFulfillmentType] = useState('PICKUP'); // PICKUP or DELIVERY
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryDistance, setDeliveryDistance] = useState(null);
  const [deliveryPlaceId, setDeliveryPlaceId] = useState(null);
  const [deliveryUnavailable, setDeliveryUnavailable] = useState(false);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      const savedFulfillment = localStorage.getItem(FULFILLMENT_STORAGE_KEY);

      if (savedCart) setItems(JSON.parse(savedCart));

      if (savedFulfillment) {
        const fulfillment = JSON.parse(savedFulfillment);
        setFulfillmentType(fulfillment.type || 'PICKUP');
        setDeliveryAddress(fulfillment.address || '');
        setDeliveryFee(fulfillment.fee || 0);
        setDeliveryDistance(fulfillment.distance || null);
        setDeliveryPlaceId(fulfillment.placeId || null);
        setDeliveryUnavailable(!!fulfillment.unavailable);
        setPickupDate(fulfillment.pickupDate || '');
        setPickupTime(fulfillment.pickupTime || '');
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  // Save fulfillment info to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        FULFILLMENT_STORAGE_KEY,
        JSON.stringify({
          type: fulfillmentType,
          address: deliveryAddress,
          fee: deliveryFee,
          distance: deliveryDistance,
          placeId: deliveryPlaceId,
          unavailable: deliveryUnavailable,
          pickupDate,
          pickupTime
        })
      );
    } catch (error) {
      console.error('Error saving fulfillment to localStorage:', error);
    }
  }, [
    fulfillmentType,
    deliveryAddress,
    deliveryFee,
    deliveryDistance,
    deliveryPlaceId,
    deliveryUnavailable,
    pickupDate,
    pickupTime
  ]);

  /**
   * Add item to cart
   * - gramsOrQty: per piatti a peso = grammi (100..1000)
   * - isFixedPrice: true per prodotti a prezzo fisso / bundle
   * - options: campi opzionali per personalizzazioni piatto
   *   esempio: { senza_lattosio: true }
   */
  const addItem = useCallback((dish, gramsOrQty, isFixedPrice = false, options = {}) => {
    setItems((prevItems) => {
      const grams = isFixedPrice ? 1 : gramsOrQty; // fixed => 1 unit
      const quantityToAdd = 1;
      const senzaLattosio = !!options?.senza_lattosio;

      // prezzo robusto
      const unitPrice = toNumber(
        dish?.calculatedPrice ??
          dish?.price_value ??
          dish?.price ??
          dish?.unitPrice ??
          0
      );

      // dishId robusto: se è bundle puoi usare stringa unica
      const dishId = dish?.id ?? dish?.dishId ?? dish?.name;

      // Check if same item exists
      const existingIndex = prevItems.findIndex(
        (item) =>
          item.dishId === dishId &&
          item.grams === grams &&
          item.isFixedPrice === !!isFixedPrice &&
          !!item.senza_lattosio === senzaLattosio
      );

      if (existingIndex !== -1) {
        const newItems = [...prevItems];
        newItems[existingIndex].quantity += quantityToAdd;
        newItems[existingIndex].lineTotal = toNumber(
          newItems[existingIndex].unitPrice * newItems[existingIndex].quantity
        );
        return newItems;
      }

      const newItem = {
        id: `${dishId}-${grams}-${senzaLattosio ? 'sl' : 'nl'}-${Date.now()}`,
        dishId,
        name: dish?.name || 'Prodotto',
        description: dish?.description || '',
        grams,
        quantity: quantityToAdd,
        unitPrice,
        lineTotal: toNumber(unitPrice * quantityToAdd),
        price300g: dish?.price300g ?? null,
        pricePerKg: dish?.pricePerKg ?? null,
        isFixedPrice: !!isFixedPrice,
        senza_lattosio: senzaLattosio
      };

      return [...prevItems, newItem];
    });
  }, []);

  // Remove item from cart
  const removeItem = useCallback((itemId) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  }, []);

  // Update item quantity
  const updateQuantity = useCallback(
    (itemId, quantity) => {
      if (quantity <= 0) {
        removeItem(itemId);
        return;
      }

      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId
            ? { ...item, quantity, lineTotal: toNumber(item.unitPrice * quantity) }
            : item
        )
      );
    },
    [removeItem]
  );

  // Clear cart
  const clearCart = useCallback(() => {
    setItems([]);
    setDeliveryAddress('');
    setDeliveryFee(0);
    setDeliveryDistance(null);
    setDeliveryPlaceId(null);
    setDeliveryUnavailable(false);
    setPickupDate('');
    setPickupTime('');
    setCustomerInfo({ name: '', phone: '', email: '', notes: '' });
  }, []);

  // Calculate subtotal
  const subtotal = items.reduce((sum, item) => sum + toNumber(item.lineTotal), 0);

  // Calculate total
  const total = subtotal + (fulfillmentType === 'DELIVERY' ? toNumber(deliveryFee) : 0);

  // Item count
  const itemCount = items.reduce((sum, item) => sum + toNumber(item.quantity), 0);

  // Check if cart is empty
  const isEmpty = items.length === 0;

  // Toggle cart visibility
  const toggleCart = useCallback(() => setIsCartOpen((prev) => !prev), []);
  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  // Check if checkout is blocked (delivery unavailable)
  const isCheckoutBlocked = fulfillmentType === 'DELIVERY' && deliveryUnavailable;

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    subtotal,
    total,
    itemCount,
    isEmpty,
    fulfillmentType,
    setFulfillmentType,
    deliveryAddress,
    setDeliveryAddress,
    deliveryFee,
    setDeliveryFee,
    deliveryDistance,
    setDeliveryDistance,
    deliveryPlaceId,
    setDeliveryPlaceId,
    deliveryUnavailable,
    setDeliveryUnavailable,
    isCheckoutBlocked,
    pickupDate,
    setPickupDate,
    pickupTime,
    setPickupTime,
    customerInfo,
    setCustomerInfo,
    isCartOpen,
    toggleCart,
    openCart,
    closeCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};